import { useEffect } from "react";
import { imageUrl } from "@/lib/api";

/**
 * Applies product SEO metadata to the document while the product page is mounted:
 *  - <title>
 *  - meta description
 *  - keywords
 *  - Open Graph (title, description, image, url, type)
 *  - Twitter card
 *  - JSON-LD Product schema
 *  - JSON-LD FAQPage schema (for Google rich results)
 *
 * All injected elements carry data-seo="product" so unmount cleans them up.
 */
export function useProductSeo(product) {
  useEffect(() => {
    if (!product) return;

    const title = product.seo_title || product.name;
    const description =
      product.meta_description ||
      product.seo_description ||
      product.short_description ||
      "";
    const ogTitle = product.og_title || title;
    const ogDescription = product.og_description || description;
    const image = imageUrl(product.images?.[product.primary_image_index || 0] || product.images?.[0] || "");
    const keywords = (product.seo_tags || []).concat(product.focus_keyword ? [product.focus_keyword] : []).join(", ");

    const prevTitle = document.title;
    document.title = title ? `${title} | Hyderabad Crackers` : prevTitle;

    const upsertMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.dataset.seo = "product";
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };

    const upsertLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"][data-seo="product"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        el.dataset.seo = "product";
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
      return el;
    };

    const metas = [
      upsertMeta('meta[name="description"][data-seo="product"]', { name: "description", content: description }),
      keywords ? upsertMeta('meta[name="keywords"][data-seo="product"]', { name: "keywords", content: keywords }) : null,
      upsertMeta('meta[property="og:type"][data-seo="product"]', { property: "og:type", content: "product" }),
      upsertMeta('meta[property="og:title"][data-seo="product"]', { property: "og:title", content: ogTitle }),
      upsertMeta('meta[property="og:description"][data-seo="product"]', { property: "og:description", content: ogDescription }),
      upsertMeta('meta[property="og:url"][data-seo="product"]', { property: "og:url", content: window.location.href }),
      image ? upsertMeta('meta[property="og:image"][data-seo="product"]', { property: "og:image", content: image }) : null,
      upsertMeta('meta[name="twitter:card"][data-seo="product"]', { name: "twitter:card", content: "summary_large_image" }),
      upsertMeta('meta[name="twitter:title"][data-seo="product"]', { name: "twitter:title", content: ogTitle }),
      upsertMeta('meta[name="twitter:description"][data-seo="product"]', { name: "twitter:description", content: ogDescription }),
      image ? upsertMeta('meta[name="twitter:image"][data-seo="product"]', { name: "twitter:image", content: image }) : null,
    ].filter(Boolean);

    const canonical = upsertLink("canonical", window.location.href);

    // JSON-LD: Product
    const productLd = {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: product.name,
      description,
      image: image ? [image] : undefined,
      sku: product.id,
      brand: product.brand ? { "@type": "Brand", name: product.brand } : undefined,
      offers: {
        "@type": "Offer",
        priceCurrency: "INR",
        price: product.price,
        availability: product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        url: window.location.href,
      },
    };
    const productScript = document.createElement("script");
    productScript.type = "application/ld+json";
    productScript.dataset.seo = "product";
    productScript.text = JSON.stringify(productLd);
    document.head.appendChild(productScript);

    // JSON-LD: FAQPage (only when at least one Q&A)
    const faqs = (product.seo_faq || []).filter((f) => f?.question && f?.answer);
    let faqScript = null;
    if (faqs.length) {
      const faqLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      };
      faqScript = document.createElement("script");
      faqScript.type = "application/ld+json";
      faqScript.dataset.seo = "product";
      faqScript.text = JSON.stringify(faqLd);
      document.head.appendChild(faqScript);
    }

    return () => {
      document.title = prevTitle;
      metas.forEach((m) => m?.remove());
      canonical.remove();
      productScript.remove();
      faqScript?.remove();
    };
  }, [product]);
}
